import { useRegisterSW } from 'virtual:pwa-register/react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Fallback poll cadence for idle clients. The primary signal is now the
// `rdm:build-changed` event fired by api.ts whenever an API response's
// X-Build-Hash header differs from the one we saw on first load — that
// delivers ~1s deploy propagation as long as the rep is making API calls
// (which is essentially always during a visit). This interval is just the
// safety net for clients sitting completely idle.
const CHECK_INTERVAL = 30 * 1000; // 30s

// Fallback reload window. vite-plugin-pwa's updateServiceWorker(true) relies
// on the browser's `controllerchange` event to call window.location.reload().
// iOS Safari in PWA-standalone mode fires that event unreliably (#1966) —
// the SW activates fine but the reload never happens, leaving the user with
// a sticky banner that no longer responds. If controllerchange hasn't
// triggered a reload within this many ms, we force one ourselves.
const RELOAD_FALLBACK_MS = 1500;

export default function UpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const location = useLocation();
  const [updating, setUpdating] = useState(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration || null;
      // Periodically check for SW updates (for users who keep app open)
      if (registration) {
        setInterval(() => {
          registration.update();
        }, CHECK_INTERVAL);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  // Check for updates when user returns to the app (tab focus, app resume)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && registrationRef.current) {
        registrationRef.current.update();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Check for updates on route navigation (catches login → home transition)
  useEffect(() => {
    if (registrationRef.current) {
      registrationRef.current.update();
    }
  }, [location.pathname]);

  // Real-time deploy detection: when api.ts sees X-Build-Hash change on any
  // response, fire registration.update() immediately. The SW will fetch the
  // new bundle and trigger the needRefresh banner within a second or two.
  useEffect(() => {
    const handleBuildChanged = () => {
      if (registrationRef.current) {
        registrationRef.current.update();
      }
    };
    window.addEventListener('rdm:build-changed', handleBuildChanged);
    return () => window.removeEventListener('rdm:build-changed', handleBuildChanged);
  }, []);

  const handleUpdate = () => {
    if (updating) return;
    setUpdating(true);
    // Trigger the normal SW skipWaiting + controllerchange-driven reload.
    void updateServiceWorker(true);
    // iOS Safari fallback: if controllerchange doesn't fire within the
    // window, force the reload ourselves. If the platform reload already
    // ran, the page is gone and this is a no-op; if not, we get the rep
    // off the stale bundle anyway.
    setTimeout(() => {
      window.location.reload();
    }, RELOAD_FALLBACK_MS);
  };

  if (!needRefresh) return null;

  return (
    <div className="pointer-events-auto slide-up">
      <div className="flex items-center gap-3 rounded-xl bg-primary-700 px-4 py-3 shadow-lg">
        <div className="flex-1">
          <p className="text-sm font-medium text-white">New version available</p>
          <p className="text-xs text-primary-200">
            {updating ? 'Reloading the app...' : 'Tap update to get the latest features'}
          </p>
        </div>
        <button
          onClick={handleUpdate}
          disabled={updating}
          className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 active:scale-95 disabled:opacity-70 disabled:cursor-wait"
        >
          {updating ? 'Updating...' : 'Update'}
        </button>
      </div>
    </div>
  );
}
